"use client";

import { useEffect, useState } from "react";

export type GalleryPhoto = {
  name: string;
  thumb: string;
  view: string;
  original: string;
};

export type GalleryEvent = {
  folderName: string;
  title: string;
  images: GalleryPhoto[];
  cover: string;
};

type GalleryBoardProps = {
  events: GalleryEvent[];
};

function FallbackImage({
  preferred,
  fallback,
  alt,
  className,
  onClick,
}: {
  preferred: string;
  fallback: string;
  alt: string;
  className?: string;
  onClick?: () => void;
}) {
  const [failedSrc, setFailedSrc] = useState("");
  const src = failedSrc === preferred ? fallback : preferred;

  return (
    <img
      src={src}
      alt={alt}
      className={className}
      loading="lazy"
      decoding="async"
      onClick={onClick}
      onError={() => {
        if (src !== fallback) setFailedSrc(preferred);
      }}
    />
  );
}

export default function GalleryBoard({ events }: GalleryBoardProps) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [images, setImages] = useState<GalleryPhoto[]>([]);
  const [index, setIndex] = useState(0);
  const [autoplay, setAutoplay] = useState(true);
  const [readySrc, setReadySrc] = useState("");
  const [fallbackFor, setFallbackFor] = useState("");

  useEffect(() => {
    if (!open || !autoplay || images.length === 0) return;

    const timer = window.setInterval(() => {
      setIndex((current) => (current + 1) % images.length);
    }, 3000);

    return () => window.clearInterval(timer);
  }, [open, autoplay, images.length]);

  function openEventGallery(event: GalleryEvent) {
    setTitle(event.title);
    setImages(event.images);
    setIndex(0);
    setReadySrc("");
    setFallbackFor("");
    setAutoplay(true);
    setOpen(true);
  }

  function pauseForUser() {
    setAutoplay(false);
  }

  function navigateManual(direction: number) {
    pauseForUser();
    setIndex((current) => {
      const next = current + direction;
      if (next >= images.length) return 0;
      if (next < 0) return images.length - 1;
      return next;
    });
  }

  function toggleAutoplay() {
    setAutoplay((current) => !current);
  }

  function closeLightbox() {
    setOpen(false);
    setAutoplay(false);
  }

  const currentPhoto = images[index];
  const imageSrc = currentPhoto
    ? fallbackFor === currentPhoto.view
      ? currentPhoto.original
      : currentPhoto.view
    : "";
  const mainReady = Boolean(imageSrc) && readySrc === imageSrc;

  function preloadNext() {
    if (images.length < 2) return;
    const next = images[(index + 1) % images.length];
    const preload = new window.Image();
    preload.src = next.view;
  }

  function handleMainError() {
    if (!currentPhoto || imageSrc === currentPhoto.original) return;
    setFallbackFor(currentPhoto.view);
  }

  return (
    <>
      <div className="gallery-section">
        <h2 className="gallery-title">Samaj Memorabilia</h2>
        <p className="gallery-subtitle">Glimpses of Agroha Vikas Trust Events & Celebrations</p>

        <div className="gallery-grid">
          {events.map((event) => (
              <div
                className="gallery-card polaroid-card"
                key={event.folderName}
                onClick={() => openEventGallery(event)}
              >
                <div className="image-wrapper">
                  <FallbackImage
                    preferred={event.cover}
                    fallback={event.images[0]?.original || event.cover}
                    className="active"
                    alt={event.title}
                  />
                  <span className="gallery-overlay">View album</span>
                </div>
                <div className="photo-info">
                  <div>
                    <h4>{event.title}</h4>
                    <p>Event Gallery</p>
                  </div>
                  <span className="view-count">{event.images.length} Photos</span>
                </div>
              </div>
            ))
          }
        </div>
      </div>

      <div id="galleryModal" className="lightbox-modal" style={{ display: open ? "flex" : "none" }}>
        <span className="modal-close" onClick={closeLightbox}>
          &times;
        </span>
        <span className="arrow arrow-left" onClick={() => navigateManual(-1)}>
          &#10094;
        </span>
        {open && imageSrc && !mainReady ? <span className="lightbox-pending" aria-hidden="true" /> : null}
        {imageSrc ? (
          <img
            id="modalMainImage"
            className="lightbox-content"
            src={imageSrc}
            alt={title}
            decoding="async"
            fetchPriority="high"
            onLoad={() => {
              setReadySrc(imageSrc);
              preloadNext();
            }}
            onError={handleMainError}
          />
        ) : null}
        <span className="arrow arrow-right" onClick={() => navigateManual(1)}>
          &#10095;
        </span>
        <div id="modalCaption" className="lightbox-caption">
          {title}
        </div>
        <div className="slide-controls">
          <span>
            Status:{" "}
            <b id="slideStatus" style={{ color: autoplay ? "#4CAF50" : "#f44336" }}>
              {autoplay ? "AUTOPLAY RUNNING" : "PAUSED (USER CONTROL)"}
            </b>
          </span>
          <button type="button" className="btn-status" id="playPauseBtn" onClick={toggleAutoplay}>
            {autoplay ? "Pause ⏸" : "Resume ▶"}
          </button>
        </div>
        <div id="thumbStrip" className="thumbnail-container">
          {images.map((image, imageIndex) => (
            <FallbackImage
              key={image.name}
              preferred={image.thumb}
              fallback={image.original}
              className={imageIndex === index ? "thumb active" : "thumb"}
              alt=""
              onClick={() => {
                pauseForUser();
                setIndex(imageIndex);
              }}
            />
          ))}
        </div>
      </div>
    </>
  );
}
